<script>
    import { onMount } from "svelte";
    import {
        user,
        userPreferences,
        createAccount,
        loggedInDoctorDetail,
        userLoginDetails,
    } from "../../stores";
    import { v4 as uuidv4 } from "uuid";

    import Input from "./components/SigninInput.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        goToURL,
        refreshTokenAfterSignIn,
        serverUrl,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { X_Device_ID } from "../../Utils/utilConstants.svelte";

    import { isMobile } from "../../stores";
    import { PageLoading } from "../../Components/Common/index.svelte";
    import { cacheDefaultFilters } from "../../Utils/utilCache.svelte";

    let inputEmail,
        inputPwd,
        rememberMe = false,
        isLoading = false,
        loginError,
        agreementNeeded = false,
        error;

    let isFromSignUpPage = false,
        showPassword = false,
        tempUserDetail = {};

    let showRedirectionModal = false,
        loginDetails = {};

    let isCheckingSSO = false;

    onMount(async () => {
        isCheckingSSO = true;

        inputEmail = $userPreferences.savedUserName;

        if (!$user.uniqueId) {
            $user.uniqueId = uuidv4();
        }

        tempUserDetail = JSON.parse(localStorage.getItem("TciTempUserDetail"));

        if (tempUserDetail?.email) _syncEmailAndPwd();

        const params = window.location.href.split("?")[1];

        if (params) {
            const paramsArr = params.split("&");

            const tempToken = paramsArr
                .find((param) => param.includes("sso="))
                .replace("sso=", "");

            const redirectURL = paramsArr.some((para) => para.includes("go="))
                ? `/${paramsArr
                      .filter((para) => !para.includes("sso="))
                      .join("&")
                      .replace("go=", "")}`
                : "/";

            await apiMethods.getMethod
                .getSSOPermission(tempToken)
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to get SSO Permission, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else handleLogin(res, {}, redirectURL);
                })
                .catch((err) =>
                    showError(
                        "Failed to get SSO Permission, please refresh the page and try again and contact support if the issue persists.",
                    ),
                );

            isCheckingSSO = false;
        } else isCheckingSSO = false;
    });

    const signIn = () => {
        error = "";

        const data = {
            email: inputEmail,
            password: inputPwd,
        };

        let emailValidation = tempUserDetail?.email
            ? document.getElementById("createAccountEmailInput")
            : document.getElementById("Email").validity.valid;

        let passwordValidation = true; // implement later

        if (emailValidation && passwordValidation) {
            isLoading = true;
            apiMethods.postMethod
                .logIn(data)
                .then((res) => handleLogin(res, data))
                .catch((err) => {
                    loginError = err;
                    isLoading = false;
                });
        } else isLoading = false;

        if (!tempUserDetail?.email)
            _updateInputStyle(emailValidation, passwordValidation);
    };

    const handleLogin = async (res, loginData, redirectURL) => {
        isLoading = false;

        if (res.error) {
            if (res.error.toLowerCase() == "mfanotprovided") {
                goToURL("/mfa-auth");
                $userLoginDetails = { ...loginData };
            } else {
                loginError = res.error;
                agreementNeeded = res.agreementNeeded ?? false;
            }
        } else {
            $user.token = res.token;
            $user.envSettings = res.envSettings ?? {};

            loginDetails = { ...res };

            var requestObject = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Credentials": "true",
                    "Access-Control-Allow-Origin": "*",
                    "Access-Control-Allow-Methods": "GET",
                    Authorization: `Bearer ${res.token}`,
                },
                mode: "cors",
                cache: "no-cache",
            };

            requestObject.headers["X-Device-Id"] = X_Device_ID;

            const response = await fetch(
                `${serverUrl}/User/${res.id}`,
                requestObject,
            );

            const result = await response.json();

            // sync user settings
            $user.settings = result.settings ? JSON.parse(result.settings) : {};
            $user.groupId = result.groupId;
            $user.groupName = result.groupName;
            $user.defaultFilters = result.defaultFilters
                ? result.defaultFilters
                : null;
            $user.email = inputEmail;

            if (result.id == res.id) {
                result.role = res.role;

                if (
                    res.role > 2 ||
                    (result.userAccounts && result.userAccounts.length > 1)
                )
                    await _handleUserDetails(result, redirectURL);
                else if (result.userAccounts.length == 1) {
                    const accountResponse = await fetch(
                        `${serverUrl}/Account/${result.userAccounts[0].id}`,
                        requestObject,
                    );

                    const accountResult = await accountResponse.json();

                    if (res.role == 2)
                        $loggedInDoctorDetail = { ...accountResult };

                    if (accountResult.status != 0 && accountResult.status != 1)
                        error =
                            "We're still working on creating your account. Please check again later.";
                    else await _handleUserDetails(result, redirectURL);
                } else await _handleUserDetails(result, redirectURL);
            }
        }
    };

    const loadDoctorDetails = (id) => {
        apiMethods.getMethod.getAccountByUserId(id).then((res) => {
            $loggedInDoctorDetail = { ...res };
            $user.groupId = res.groupId;
            $user.groupName = res.groupName;
        });
    };

    const goToCreateAccount = () => {
        goToURL("/sign-up");
    };

    const goToSignUp = () => {
        goToCreateAccount();

        $createAccount = {};

        loginDetails = {};
    };

    const clearError = () => {
        loginError = null;
    };

    const closeRedirectionModal = () => {
        showRedirectionModal = false;
    };

    const continueRedirection = () => {
        closeRedirectionModal();

        loginDetails = {};

        goToCreateAccount();
    };

    // HELPER FUNCTIONS
    const _updateInputStyle = (emailValid, pwdValid) => {
        const emailTag = document.getElementsByTagName("label")[0];
        const pwdTag = document.getElementsByTagName("label")[1];
        const emailInput = document.getElementById("Email");
        const pwdInput = document.getElementById("Password");

        if (emailValid) {
            emailTag.style.color = null;
            emailInput.style.color = null;
            emailInput.style.border = null;
        } else {
            emailTag.style.color = "#E05757";
            emailInput.style.color = "#E05757";
            emailInput.style.border = "2px solid #E05757";
        }

        if (pwdValid) {
            pwdTag.style.color = null;
            pwdInput.style.color = null;
            pwdInput.style.border = null;
        } else {
            pwdTag.style.color = "#E05757";
            pwdInput.style.color = "#E05757";
            pwdInput.style.border = "2px solid #E05757";
        }
    };

    const _syncEmailAndPwd = () => {
        isFromSignUpPage = true;

        inputEmail = tempUserDetail.email;
        inputPwd = tempUserDetail.password;

        setTimeout(() => {
            localStorage.removeItem("TciTempUserDetail");
        }, 1000);
    };

    const _keepShowPasswordStatus = () => {
        let el = document.getElementById("createAccountPwdInput");
        if (showPassword) el.type = "text";
        else el.type = "password";
    };

    const _toggleShowPassword = () => {
        showPassword = !showPassword;
        _keepShowPasswordStatus();
    };

    const _handleUserDetails = async (res, redirectURL) => {
        if (
            (res.role == 2 || !res.role) &&
            (!res.verifiedEmail ||
                !res.verifiedPhone ||
                !res.userAccounts.length)
        ) {
            showRedirectionModal = true;

            $createAccount = { ...res };

            if (loginDetails.role == 2) loadDoctorDetails($createAccount.id);
        } else {
            $user.refreshToken = loginDetails.refreshToken;
            $user.expiration = loginDetails.expiration;
            $user.id = loginDetails.id;
            $user.name = loginDetails.name;
            $user.role = loginDetails.role;

            await refreshTokenAfterSignIn();

            // No need for verified doctors as doctor and account details are loaded in prev function
            // if (loginDetails.role == 2) loadDoctorDetails($user.id);

            if (rememberMe) $userPreferences.savedUserEmail = inputEmail;
            else $userPreferences.savedUserEmail = null;

            loginDetails = {};

            await cacheDefaultFilters();

            if (redirectURL) {
                goToURL(`${redirectURL}`);
            } else {
                location.hash = "";
            }
        }
    };
</script>

<div class="flex-1 flex flex-col items-center w-full h-full p-6 showBG">
    <div class="flex items-center px-4 py-2">
        <img src="/favicon.png" alt="" class="w-[48px] h-[48px]" />
        <p class="text-primary text-2xl font-bold title-font pl-4">Anaemate</p>
    </div>

    {#if isCheckingSSO}
        <PageLoading />
    {:else}
        <div class="flex flex-1 flex-col items-center justify-center gap-10 w-full">
            <div class="artboard artboard-demo h-auto gap-4 p-6 shadow-lg !bg-[#ecf5fb] {$isMobile
                    ? 'w-full'
                    : 'w-[500px]'}"
            >
                <div class="flex flex-col gap-y-4">
                    <p class="text-[24px] font-bold">
                        <span class="text-gray-600">Are you a patient?</span>
                        
                    </p>
                    <p class="font-medium text-[16px]">
                        <span class="text-gray-600">Are you looking to pay or download an invoice? If you have been sent a link, please try opening the full link again. This login page is for doctors.</span>
                    </p>
                    <button
							class="btn btn-secondary"
							on:click|preventDefault={() =>
								window.location.href = 'https://www.anaemate.com.au/contact-us'
                                }
							>Get Help</button
						>
                </div>
            </div>
            <div
                class="artboard artboard-demo h-auto gap-4 p-6 shadow-lg {$isMobile
                    ? 'w-full'
                    : 'w-[500px]'}"
            >
                <div class="flex flex-col items-center gap-y-4">
                    <p class="text-[24px] font-bold">
                        <span class="text-gray-600">Sign In to</span>
                        <span class="text-primary">Anaemate</span>
                    </p>
                    <p class="font-medium text-[16px]">
                        <span class="text-gray-600">New Here?</span>
                        <span
                            class="text-gray-600 font-bold cursor-pointer hover:underline"
                            on:click|preventDefault={goToSignUp}
                        >
                            Create an Account
                        </span>
                    </p>
                </div>
                <form
                    on:submit|preventDefault={signIn}
                    novalidate
                    class="w-full"
                >
                    <!-- <p class="text-3xl font-bold my-8">Sign In</p> -->

                    {#if !isFromSignUpPage}
                        <Input
                            label="Email"
                            bind:value={inputEmail}
                            type="email"
                            error={loginError}
                            inputClass="w-full"
                            {clearError}
                            autofocus
                            isAutoComplete="off"
                        />

                        <Input
                            label="Password"
                            bind:value={inputPwd}
                            type="password"
                            showForgotPwd={true}
                            error={loginError}
                            inputClass="w-full"
                            {clearError}
                            isAutoComplete="off"
                        />
                    {:else}
                        <div class="flex flex-col gap-y-2">
                            <label
                                for="emailAddress"
                                class="leading-7 text-sm text-gray-600 font-bold text-[16px]"
                            >
                                Email
                            </label>

                            <input
                                type="email"
                                id="createAccountEmailInput"
                                name="emailAddress"
                                class="input input-sm border outline-2 mb-2"
                                autocomplete="off"
                                bind:value={inputEmail}
                            />
                        </div>

                        <div class="flex flex-col gap-y-2">
                            <label
                                for="createAccountPwdInput"
                                class="leading-7 text-sm text-gray-600 font-bold text-[16px]"
                            >
                                Password
                            </label>

                            <div class="relative">
                                <input
                                    type="password"
                                    id="createAccountPwdInput"
                                    name="createAccountPwdInput"
                                    class="input input-sm border outline-2 mb-2 w-full"
                                    autocomplete="off"
                                    bind:value={inputPwd}
                                    on:input={_keepShowPasswordStatus}
                                />
                                {#if showPassword}
                                    <i
                                        style="font-size:16px"
                                        class="fa absolute top-1/3 right-4 hover:cursor-pointer"
                                        on:click|preventDefault={_toggleShowPassword}
                                        >&#xf070;</i
                                    >
                                {:else}
                                    <i
                                        style="font-size:16px"
                                        class="fa absolute top-1/3 right-4 hover:cursor-pointer"
                                        on:click|preventDefault={_toggleShowPassword}
                                        >&#xf06e;</i
                                    >
                                {/if}
                            </div>
                        </div>
                    {/if}

                    {#if loginError}
                        <p
                            class="font-bold my-4 whitespace-normal text-[#E05757]"
                        >
                            {#if agreementNeeded}
                                To login, you first need to agree to new documents. Please login to your mobile app or contact support at support@tcihealth.com.au
                            {:else}
                                Sign in failed! Please check your login credentials.
                            {/if} 
                        </p>
                    {:else if error}
                        <p
                            class="font-bold my-4 w-80 whitespace-normal text-[#E05757]"
                        >
                            {error}
                        </p>
                    {/if}

                    {#if isLoading}
                        <button
                            class="btn btn-md btn-primary text-white flex-none border-none w-full mt-4 loading cursor-not-allowed"
                        >
                            Signing in...
                        </button>
                    {:else}
                        <button
                            class="btn btn-md btn-primary text-white flex-none border-none w-full mt-4"
                            >Continue</button
                        >
                    {/if}
                </form>
            </div>
        </div>
    {/if}
</div>

<div
    class={showRedirectionModal ? "modal modal-open sm:modal-middle" : "modal"}
>
    <div class="modal-box">
        <h3 class="font-medium text-lg">Oops....</h3>
        <div class="p-4">
            <p class="leading-6 text-error whitespace-pre-line">
                Looks like you haven't finished the onboarding process. Would
                you like to continue?
            </p>
        </div>
        <div class="modal-action">
            <button
                class="btn btn-error text-white btn-sm border-none w-24"
                on:click={closeRedirectionModal}
            >
                Close
            </button>

            <button
                class="btn btn-secondary text-white btn-sm border-none w-24"
                on:click={continueRedirection}
            >
                Continue
            </button>
        </div>
    </div>
</div>

<style>
    .artboard {
        background-color: #f7f7f7;
        border: 1px solid rgba(65, 91, 63, 0.25);
        box-shadow: 10px 10px 30px rgba(0, 0, 0, 0.1);
        border-radius: 15px;
    }

    .showBG {
        height: 100vh;
        background-image: url("/imgs/bg_signIn.png");
        background-repeat: no-repeat;
        background-attachment: fixed;
        background-position: bottom;
        background-size: contain;
    }
</style>
